/**
 * SOPs Page
 *
 * Management interface for Standard Operating Procedures with
 * filtering, pagination, and AI parsing status indicators.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Upload,
  Search,
  Filter,
  CheckCircle2,
  AlertTriangle,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  Eye,
  Building2,
  X,
  FileWarning,
  Trash2,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { useSOPs, useDeleteSop } from '../../hooks/useSOPs';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { SOPUploadModal } from '../../components/sops/SOPUploadModal';
import { SopBuilderModal } from './SopBuilderModal';
import { useSocketRefetch } from '../../lib/socket';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useMemo(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function SOPsPage() {
  useDocumentTitle('Documents');
  // Filter state
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [parsedFilter, setParsedFilter] = useState<'all' | 'parsed' | 'unparsed'>('all');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Modal state
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Delete mutation
  const deleteSop = useDeleteSop();

  // Handler for successful SOP upload - refresh the list
  const handleUploadSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['sops'] });
  }, [queryClient]);

  // Auto-refresh when SOP processing completes or fails (via Socket.io)
  useSocketRefetch(['sop:completed', 'sop:failed'], () => {
    queryClient.invalidateQueries({ queryKey: ['sops'] });
  });

  // Debounce search
  const debouncedSearch = useDebounce(search, 300);

  // Build query options
  const queryOptions = useMemo(() => {
    const options: {
      search?: string;
      departmentId?: string;
      isActive?: boolean;
      page: number;
      limit: number;
    } = { page, limit };

    if (debouncedSearch) options.search = debouncedSearch;
    if (departmentId) options.departmentId = departmentId;
    if (statusFilter === 'active') options.isActive = true;
    if (statusFilter === 'inactive') options.isActive = false;

    return options;
  }, [debouncedSearch, departmentId, statusFilter, page, limit]);

  // Fetch data
  const { data: sopsData, isLoading, error } = useSOPs(queryOptions);
  const { data: departments } = useDepartmentLookup();

  // Check if any SOP is processing to enable polling
  const hasProcessingSops = useMemo(() => {
    const activeStates = [
      'pending', // Include pending - worker will pick it up soon
      'structure_detection',
      'extracting_images',
      'rendering_pages',
      'extracting_tasks',
      'generating_questions',
      'generating_embeddings',
    ];
    const sops = sopsData?.data ?? [];
    return sops.some((s) => activeStates.includes(s.processingStatus));
  }, [sopsData?.data]);

  // Auto-poll when there are processing SOPs
  useEffect(() => {
    if (!hasProcessingSops) return;

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['sops'] });
    }, 3000);

    return () => clearInterval(interval);
  }, [hasProcessingSops, queryClient]);

  // Filter by parsed status client-side (not in API)
  const filteredSops = useMemo(() => {
    const sops = sopsData?.data ?? [];
    if (parsedFilter === 'all') return sops;
    if (parsedFilter === 'parsed') return sops.filter((s) => s.hasParsedSteps);
    return sops.filter((s) => !s.hasParsedSteps);
  }, [sopsData?.data, parsedFilter]);

  const pagination = sopsData?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const total = pagination?.total ?? 0;

  // Calculate showing range
  const showingFrom = total === 0 ? 0 : (page - 1) * limit + 1;
  const showingTo = Math.min(page * limit, total);

  // Handlers
  const handleClearFilters = useCallback(() => {
    setSearch('');
    setDepartmentId('');
    setStatusFilter('all');
    setParsedFilter('all');
    setPage(1);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteSop.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Delete failed:', error);
      // Error will be shown via mutation state
    }
  }, [deleteTarget, deleteSop]);

  const hasActiveFilters = Boolean(search || departmentId || statusFilter !== 'all' || parsedFilter !== 'all');

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('ellipsis');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
              <FileText size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Library
            </h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Standard Operating Procedures for automated performance assessment
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsBuilderOpen(true)}
            className="
              inline-flex items-center gap-2 rounded-xl
              border border-guild-green/30 bg-white px-5 py-2.5
              text-sm font-semibold text-guild-green
              shadow-sm
              transition-all duration-200
              hover:bg-guild-mint/20 hover:border-guild-green/50 hover:shadow-md
              active:scale-[0.98]
            "
          >
            <Sparkles size={18} strokeWidth={2.5} />
            Create with AI
          </button>
          <button
            onClick={() => setIsUploadOpen(true)}
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/25
              transition-all duration-200
              hover:bg-guild-green/90 hover:shadow-xl hover:shadow-guild-green/30
              active:scale-[0.98]
            "
          >
            <Upload size={18} strokeWidth={2.5} />
            Upload SOP
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search by title..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="
                w-full rounded-xl border border-gray-200 bg-gray-50/50
                py-2.5 pl-10 pr-4
                text-sm text-gray-900 placeholder-gray-400
                transition-all duration-200
                focus:border-guild-green focus:bg-white focus:outline-none focus:ring-2 focus:ring-guild-green/20
              "
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Filter size={16} />
              <span className="hidden sm:inline">Filters:</span>
            </div>

            {/* Department Filter */}
            <div className="relative">
              <Building2
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <select
                value={departmentId}
                onChange={(e) => {
                  setDepartmentId(e.target.value);
                  setPage(1);
                }}
                className="
                  appearance-none rounded-lg border border-gray-200 bg-white
                  py-2 pl-9 pr-8
                  text-sm text-gray-700
                  transition-colors duration-200
                  hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                "
              >
                <option value="">All Departments</option>
                {departments?.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                setPage(1);
              }}
              className="
                appearance-none rounded-lg border border-gray-200 bg-white
                py-2 px-3 pr-8
                text-sm text-gray-700
                transition-colors duration-200
                hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              "
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* Parsed Filter */}
            <select
              value={parsedFilter}
              onChange={(e) => {
                setParsedFilter(e.target.value as 'all' | 'parsed' | 'unparsed');
                setPage(1);
              }}
              className="
                appearance-none rounded-lg border border-gray-200 bg-white
                py-2 px-3 pr-8
                text-sm text-gray-700
                transition-colors duration-200
                hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              "
            >
              <option value="all">All Parsing</option>
              <option value="parsed">Has Steps</option>
              <option value="unparsed">Needs Parsing</option>
            </select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="
                  flex items-center gap-1.5 rounded-lg
                  px-3 py-2
                  text-sm font-medium text-gray-500
                  transition-colors duration-200
                  hover:bg-gray-100 hover:text-gray-700
                "
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message="Failed to load library" />
        ) : filteredSops.length === 0 ? (
          <EmptyState hasFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Title
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Version
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Department
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Jobs
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Assessments
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredSops.map((sop, index) => (
                    <tr
                      key={sop.id}
                      className="group transition-colors duration-150 hover:bg-guild-mint/5"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <td className="px-6 py-4">
                        <Link
                          to={`/sops/${sop.id}`}
                          className="
                            font-semibold text-gray-900
                            transition-colors duration-200
                            hover:text-guild-green
                          "
                        >
                          {sop.title}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-mono font-medium text-gray-600">
                          v{sop.version}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {sop.departmentName || (
                            <span className="text-gray-400 italic">Unassigned</span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge isActive={sop.isActive} />
                      </td>
                      <td className="px-6 py-4">
                        <JobsBadge count={sop.jobCount} processingStatus={sop.processingStatus} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <ClipboardCheck size={14} className="text-gray-400" />
                          {sop.assessmentCount}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/sops/${sop.id}`}
                            className="
                              rounded-lg p-2
                              text-gray-400
                              transition-all duration-200
                              hover:bg-guild-mint/20 hover:text-guild-green
                            "
                            title="View details"
                          >
                            <Eye size={16} />
                          </Link>
                          <button
                            onClick={() => setDeleteTarget({ id: sop.id, title: sop.title })}
                            disabled={sop.assessmentCount > 0}
                            className="
                              rounded-lg p-2
                              text-gray-400
                              transition-all duration-200
                              hover:bg-red-50 hover:text-red-500
                              disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400
                            "
                            title={sop.assessmentCount > 0 ? 'Cannot delete SOP with assessments' : 'Delete SOP'}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col items-center justify-between gap-4 border-t border-gray-100 bg-gray-50/30 px-6 py-4 sm:flex-row">
              <p className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-700">{showingFrom}</span> to{' '}
                <span className="font-medium text-gray-700">{showingTo}</span> of{' '}
                <span className="font-medium text-gray-700">{total}</span> items
              </p>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="
                    flex items-center gap-1 rounded-lg px-3 py-2
                    text-sm font-medium text-gray-600
                    transition-colors duration-200
                    hover:bg-white hover:text-gray-900
                    disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent
                  "
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                <div className="flex items-center gap-1 px-2">
                  {getPageNumbers().map((pageNum, idx) =>
                    pageNum === 'ellipsis' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`
                          min-w-[36px] rounded-lg px-3 py-2
                          text-sm font-medium
                          transition-all duration-200
                          ${
                            page === pageNum
                              ? 'bg-guild-green text-white shadow-sm'
                              : 'text-gray-600 hover:bg-white hover:text-gray-900'
                          }
                        `}
                      >
                        {pageNum}
                      </button>
                    )
                  )}
                </div>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="
                    flex items-center gap-1 rounded-lg px-3 py-2
                    text-sm font-medium text-gray-600
                    transition-colors duration-200
                    hover:bg-white hover:text-gray-900
                    disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent
                  "
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !deleteSop.isPending && setDeleteTarget(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete SOP?</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to permanently delete{' '}
              <span className="font-semibold text-gray-900">"{deleteTarget.title}"</span>?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              This will also delete the PDF file from storage and all associated steps and questions.
            </p>
            {deleteSop.isError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                {deleteSop.error instanceof Error ? deleteSop.error.message : 'Failed to delete SOP'}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteSop.isPending}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteSop.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleteSop.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {isUploadOpen && (
        <SOPUploadModal
          isOpen={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* AI SOP Builder Modal */}
      <SopBuilderModal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        onCreated={handleUploadSuccess}
      />
    </div>
  );
}

// Status Badge Component
function StatusBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Inactive
    </span>
  );
}

// Jobs Badge Component (Jobs = Sections in the database)
function JobsBadge({
  count,
  processingStatus
}: {
  count: number;
  processingStatus: string;
}) {
  // Active processing states - show spinner
  const activeProcessingStates = [
    'pending',
    'structure_detection',
    'extracting_images',
    'rendering_pages',
    'extracting_tasks',
    'generating_questions',
    'generating_embeddings',
  ];

  if (activeProcessingStates.includes(processingStatus)) {
    // Format the status for display (e.g., "structure_detection" -> "Detecting structure")
    const statusLabels: Record<string, string> = {
      pending: 'Queued...',
      structure_detection: 'Detecting structure...',
      extracting_images: 'Extracting images...',
      rendering_pages: 'Rendering pages...',
      extracting_tasks: 'Extracting tasks...',
      generating_questions: 'Generating questions...',
      generating_embeddings: 'Creating embeddings...',
    };
    const label = statusLabels[processingStatus] || 'Processing...';

    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
        <Loader2 size={12} className="animate-spin" />
        {label}
      </span>
    );
  }

  // Failed - show error state
  if (processingStatus === 'failed') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
        <AlertTriangle size={12} />
        Failed
      </span>
    );
  }

  // Completed with jobs
  if (processingStatus === 'completed' && count > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
        <CheckCircle2 size={12} />
        {count} {count === 1 ? 'job' : 'jobs'}
      </span>
    );
  }

  // Pending or no jobs
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
      <AlertTriangle size={12} />
      Needs processing
    </span>
  );
}

// Loading State
function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-sm text-gray-500">Loading library...</p>
    </div>
  );
}

// Error State
function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
        <FileWarning size={24} className="text-red-500" />
      </div>
      <p className="mt-4 text-sm font-medium text-gray-900">Something went wrong</p>
      <p className="mt-1 text-sm text-gray-500">{message}</p>
    </div>
  );
}

// Empty State
function EmptyState({
  hasFilters,
  onClearFilters,
}: {
  hasFilters: boolean;
  onClearFilters: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50">
        <FileText size={32} className="text-gray-400" />
      </div>
      <p className="mt-4 text-sm font-medium text-gray-900">
        {hasFilters ? 'No documents found' : 'No documents yet'}
      </p>
      <p className="mt-1 text-sm text-gray-500">
        {hasFilters
          ? 'Try adjusting your filters to find what you\'re looking for.'
          : 'Upload your first SOP to enable automated performance assessment.'}
      </p>
      {hasFilters && (
        <button
          onClick={onClearFilters}
          className="
            mt-4 rounded-lg px-4 py-2
            text-sm font-medium text-guild-green
            transition-colors duration-200
            hover:bg-guild-mint/20
          "
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}

export default SOPsPage;
